import { Box, ITheme, Typography, useTheme } from '@mui/material';
import React from 'react';
import { useAppSelector } from '../../../../store/reducers/store';
import { Utilities } from '../../../../utility/utils';
import { useTranslation } from 'react-i18next';

const InvestmentsRepartitionGraph: React.FC = () => {
    const width = 570;
    const height = 150;
    const margin = 40;
    const maxHeight = 100; // Highest bar of the graph

    const { t, i18n } = useTranslation();
    
    const renderBottomLabel = (pos: number[], proportion: number, textColor: string): React.ReactNode => {
        return <foreignObject width={"80px"} height={"30px"} x={pos[0] - 40} y={pos[1] - 15}>
            <Box width={"100%"} height={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
                <Typography fontSize={"16px"} fontWeight={"600"} lineHeight={"18px"} color={textColor}>€{proportion.toLocaleString(i18n.language)}</Typography>
            </Box>
        </foreignObject>
    }

    const theme = useTheme() as ITheme;
    const investors = useAppSelector(state => state.portfolioDetails.portfolio?.tickets);
    const colors = Utilities.shuffleArray(Object.keys(theme.palette.leo).filter(k => k.indexOf("color")!== -1).map(k => (theme.palette.leo as any)[k]));

    if (investors === undefined){
        return <></>;
    }

    const min = Math.min(...investors.map(i => i.amount));
    const firstQuartile = investors[Math.floor(3*(investors.length + 1)/4)].amount;
    const median = Utilities.isEven(investors.length) ?  (investors[Math.floor(investors.length/2)].amount + investors[Math.floor((investors.length/2) + 1)].amount) / 2 : investors[(investors.length + 1) / 2].amount;
    const thirdQuartile = investors[Math.floor((investors.length + 1)/4)].amount;
    const max = Math.max(...investors.map(i => i.amount));


    return <>
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} xmlns="http://www.w3.org/2000/svg">
        <g transform={`translate(${width/2}, ${height/2})`}>
            <rect x={-width/2+margin} y={-4} width={width-2*margin} height={8} fill={theme.palette.leo.tone11} />
            <rect x={-width/2+margin} y={-33} width={16} height={66} fill={colors[0]} rx={8} ry={8} />
            <rect x={2*(-width/2+margin)/3} y={-48} width={16} height={96} fill={colors[1]} rx={8} ry={8} />
            <rect x={0} y={-maxHeight/2} width={16} height={maxHeight} fill={colors[2]} rx={8} ry={8} />
            <rect x={2*(width/2-margin)/3} y={-48} width={16} height={96} fill={colors[3]} rx={8} ry={8} />
            <rect x={width/2-margin} y={-33} width={16} height={66} fill={colors[4]} rx={8} ry={8} />
            {renderBottomLabel([-width/2+margin,45], min, colors[0])}
            {renderBottomLabel([2*(-width/2+margin)/3,60], firstQuartile, colors[1])}
            {renderBottomLabel([0,maxHeight/2+15], median, colors[2])}
            {renderBottomLabel([2*(width/2-margin)/3,60], thirdQuartile, colors[3])}
            {renderBottomLabel([width/2-margin,45], max, colors[4])}
        </g>
    </svg>
    <Box display={"flex"} flexWrap={"wrap"} gap={"18px 10px"}>
        <Box display={"flex"} gap={"13px"} flexBasis={"30%"} flexGrow={"0"}>
            <Box width={"14px"} height={"14px"} bgcolor={colors[0]} borderRadius={"7px"}></Box>
            <Typography fontSize={"14px"} lineHeight={"14px"} fontWeight={"600"} color={theme.palette.leo.tone3}>{t('PortfolioDetailsInvestmentMinSize')}</Typography>
        </Box>
        <Box display={"flex"} gap={"13px"} flexBasis={"30%"} flexGrow={"0"}>
            <Box width={"14px"} height={"14px"} bgcolor={colors[2]} borderRadius={"7px"}></Box>
            <Typography fontSize={"14px"} lineHeight={"14px"} fontWeight={"600"} color={theme.palette.leo.tone3}>{t('PortfolioDetailsInvestmentMedian')}</Typography>
        </Box>
        <Box display={"flex"} gap={"13px"} flexBasis={"30%"} flexGrow={"0"}>
            <Box width={"14px"} height={"14px"} bgcolor={colors[4]} borderRadius={"7px"}></Box>
            <Typography fontSize={"14px"} lineHeight={"14px"} fontWeight={"600"} color={theme.palette.leo.tone3}>{t('PortfolioDetailsInvestmentMaxSize')}</Typography>
        </Box>
        <Box display={"flex"} gap={"13px"} flexBasis={"30%"} flexGrow={"0"}>
            <Box width={"14px"} height={"14px"} bgcolor={colors[1]} borderRadius={"7px"}></Box>
            <Typography fontSize={"14px"} lineHeight={"14px"} fontWeight={"600"} color={theme.palette.leo.tone3}>{t('PortfolioDetailsInvestmentBottomQuartile')}</Typography>
        </Box>
        <Box display={"flex"} gap={"13px"} flexBasis={"30%"} flexGrow={"0"}>
            <Box width={"14px"} height={"14px"} bgcolor={colors[3]} borderRadius={"7px"}></Box>
            <Typography fontSize={"14px"} lineHeight={"14px"} fontWeight={"600"} color={theme.palette.leo.tone3}>{t('PortfolioDetailsInvestmentTopQuartile')}</Typography>
        </Box>
    </Box>
    </>;
}

export default InvestmentsRepartitionGraph;